<template>
  <VStepperVertical v-if="mdAndDown">
    <template v-for="(step, index) in steps" :key="step.value">
      <VStepperVerticalItem
        :complete="step.completed"
        :value="step.value"
        :disabled="true"
        elevation="0"
        :class="[
          'vertical-step-item',
          {
            'vertical-step-item--first': index === 0,
            'vertical-step-item--second-to-last': index === steps.length - 2,
            'vertical-step-item--active': step.active,
            'vertical-step-item--before-active': steps[index + 1]?.active
          }
        ]"
      >
        <template #title>
          <p
            data-testid="step-title"
            :class="['text-sm font-semibold', step.active ? 'text-brand-700' : 'text-gray-700']"
          >
            {{ step.title }}
          </p>
        </template>

        <template #subtitle>
          <p
            data-testid="step-subtitle"
            :class="['text-sm', step.active ? 'text-brand-600' : 'text-gray-600']"
          >
            {{ step.subtitle }}
          </p>
        </template>

        <template #icon="{ hasCompleted }">
          <div v-if="hasCompleted" class="completed-step-icon">
            <VIcon icon="check" />
          </div>

          <div v-else :class="step.active ? 'step-icon--active' : 'step-icon'" />
        </template>
      </VStepperVerticalItem>
    </template>
  </VStepperVertical>

  <VStepper v-else alt-labels elevation="0">
    <VStepperHeader class="shadow-none xl:mr-20">
      <template v-for="(step, index) in steps" :key="step.value">
        <VStepperItem :complete="step.completed" class="px-0 !basis-48" :disabled="true">
          <template #title>
            <p
              data-testid="step-title"
              :class="['text-sm font-semibold', { 'text-consultation-card-dark': step.active }]"
            >
              {{ step.title }}
            </p>
          </template>

          <template #subtitle>
            <p
              data-testid="step-subtitle"
              :class="['text-sm', { 'text-consultation-card': step.active }]"
            >
              {{ step.subtitle }}
            </p>
          </template>

          <template #icon="{ hasCompleted }">
            <div v-if="hasCompleted" class="completed-step-icon">
              <VIcon icon="check" />
            </div>

            <div v-else :class="step.active ? 'step-icon--active' : 'step-icon'" />
          </template>
        </VStepperItem>

        <VDivider
          v-if="index < steps.length - 1"
          :class="setDividerClass(index)"
          opacity="1"
          thickness="2"
        />
      </template>
    </VStepperHeader>
  </VStepper>
</template>

<script setup lang="ts">
import { ConsultationRequestStatusDTO } from '@/generated'
import useConsultationStepper from '@/composables/useConsultationStepper'
import { toRef } from 'vue'
import { VStepperVertical, VStepperVerticalItem } from 'vuetify/labs/VStepperVertical'
import { useDisplay } from 'vuetify'

interface Props {
  consultationStep: ConsultationRequestStatusDTO
}

const { consultationStep } = defineProps<Props>()
const reactiveStep = toRef(() => consultationStep)

const { steps } = useConsultationStepper(reactiveStep)
const { mdAndDown } = useDisplay()

function setDividerClass(index: number) {
  const isNextStepCompleted = steps.value[index + 1].completed
  const isNextStepActive = steps.value[index + 1].active

  if (isNextStepCompleted) {
    return 'step-divider--next-completed'
  } else if (isNextStepActive) {
    return 'step-divider--next-active'
  } else {
    return 'step-divider'
  }
}
</script>

<style scoped lang="scss">
$borderWidth: 2px;
$iconActiveColor: #206583;

:deep(.v-avatar) {
  background: none !important;
  overflow: visible;
  width: auto !important;
  height: auto !important;

  &.v-stepper-vertical-item__avatar {
    margin-inline-end: 12px;
  }
}

.step-divider {
  margin: 35px -84px 0 !important;
}

.step-divider--next-completed {
  @extend .step-divider;
  border-color: $iconActiveColor;
}

.step-divider--next-active {
  @extend .step-divider--next-completed;
  margin: 35px -80px 0 -84px !important;
}

.step-icon {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #fff;
  border: $borderWidth solid #d5d7da;
}

.step-icon::after {
  width: 8px;
  height: 8px;
  content: '';
  display: block;
  background-color: #d5d7da;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.step-icon--active {
  @extend .step-icon;
  background-color: $iconActiveColor;
  border: none;
  outline-offset: 2px;
  outline: 2px solid $iconActiveColor;
}

.step-icon--active::after {
  background-color: #fff;
}

.completed-step-icon {
  @extend .step-icon;
  background-color: $iconActiveColor;
  border-color: $iconActiveColor;
}

.completed-step-icon::after {
  content: none;
}

.v-stepper-vertical-item__avatar .completed-step-icon {
  border: none;
}

.vertical-step-item {
  background-color: transparent;

  &::before {
    margin-top: -1.5px;
  }

  &--first {
    &.vertical-step-item--active::before {
      margin-top: 0;
    }

    &:not(.vertical-step-item--active)::before {
      margin-top: -4px;
      height: calc(100% - 26px);
    }
  }

  &--second-to-last {
    &.vertical-step-item--active::before {
      margin-top: 2px;
    }

    &:not(.vertical-step-item--active)::before {
      height: calc(100% - 27px);
    }
  }

  &--before-active::before {
    height: calc(100% - 34px);
  }
}
</style>
